// Copyright (c) Docugami, Inc. All rights reserved.

import { configureStore } from "@reduxjs/toolkit";
import { billingApiSlice } from "billing/BillingService";
import { createLogger } from 'redux-logger';
import { Config } from '../config';
import { apiKeysSlice } from "./apiKeys/apiKeysSlice";
import { authenticationReducer } from './authentication/reducers';
import { chatApi, chatSlice } from "./chat/chatSlice";
import { clustersReducer } from './clusters/reducers';
import { samplesApiSlice } from "./clusters/samplesSlice";
import { dgmlSlice } from "./dgml/dgmlSlice";
import { documentsReducer } from './documents/reducers';
import { errorSlice } from "./errorSlice";
import { featureFlagsReducer } from './featureFlags/reducers';
import { frontendSlice } from "./frontend/frontendSlice";
import { listenerMiddleware } from "./listenerMiddleware";
import { organizationApiSlice } from "./organization/organizationSlice";
import { repeatsSlice } from "./repeatsSlice";
import { organizationReducer } from './organization/reducers';
import { initReportsStreamListener, reportOperationsSlice, reportsApiSlice, reportsSlice } from "./reports/reportsSlice";
import { sdvSlice } from "./sdvSlice";
import { settingsReducer } from './settings/reducers';
import { urlHashParametersReducer } from './urlhashparameters/reducers';
import { auth0UserApiSlice } from "./user/auth0UserSlice";
import { projectsApi, workPathsApi, workPathsSlice } from "./work_path/workPathSlice";
import { discoveryApi } from './discovery/discoverySlice';
import { extractionSlice } from './extraction/extractionSlice';
import { storageConnectionsApiSlice } from "./storage/storageConnectionsApiSlice";
import { displaySlice } from "./display/displaySlice";

export const store = configureStore({
    reducer: {
        authentication: authenticationReducer,
        clusters: clustersReducer,
        documents: documentsReducer,
        settings: settingsReducer,
        [extractionSlice.name]: extractionSlice.reducer,
        organizations: organizationReducer,
        urlHashParameters: urlHashParametersReducer,
        featureFlags: featureFlagsReducer,
        [reportsApiSlice.reducerPath]: reportsApiSlice.reducer,
        [reportOperationsSlice.name]: reportOperationsSlice.reducer,
        [workPathsSlice.name]: workPathsSlice.reducer,
        [workPathsApi.reducerPath]: workPathsApi.reducer,
        [projectsApi.reducerPath]: projectsApi.reducer,
        [billingApiSlice.reducerPath]: billingApiSlice.reducer,
        [auth0UserApiSlice.reducerPath]: auth0UserApiSlice.reducer,
        [apiKeysSlice.reducerPath]: apiKeysSlice.reducer,
        [chatApi.reducerPath]: chatApi.reducer,
        [chatSlice.name]: chatSlice.reducer,
        [dgmlSlice.reducerPath]: dgmlSlice.reducer,
        [frontendSlice.reducerPath]: frontendSlice.reducer,
        [sdvSlice.name]: sdvSlice.reducer,
        [samplesApiSlice.reducerPath]: samplesApiSlice.reducer,
        [errorSlice.name]: errorSlice.reducer,
        [reportsSlice.name]: reportsSlice.reducer,
        [organizationApiSlice.reducerPath]: organizationApiSlice.reducer,
        [repeatsSlice.reducerPath]: repeatsSlice.reducer,
        [discoveryApi.reducerPath]: discoveryApi.reducer,
        [storageConnectionsApiSlice.reducerPath]: storageConnectionsApiSlice.reducer,
        [displaySlice.name]: displaySlice.reducer,
    },
    // TODO: Please re-enable immutable check when we clean-up our redux usage
    middleware: (gDM) => gDM({ immutableCheck: false, serializableCheck: process.env.REACT_APP_ENV !== "proddev" })
        .prepend(listenerMiddleware.middleware)
        .concat(reportsApiSlice.middleware)
        .concat(billingApiSlice.middleware)
        .concat(auth0UserApiSlice.middleware)
        .concat(apiKeysSlice.middleware)
        .concat(dgmlSlice.middleware)
        .concat(chatApi.middleware)
        .concat(frontendSlice.middleware)
        .concat(samplesApiSlice.middleware)
        .concat(organizationApiSlice.middleware)
        .concat(repeatsSlice.middleware)
        .concat(projectsApi.middleware)
        .concat(workPathsApi.middleware)
        .concat(discoveryApi.middleware)
        .concat(storageConnectionsApiSlice.middleware)
        .concat(Config.verboseReduxLogging ? [createLogger() as any] : [])
});

export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Initialize listeners that need the store after it's created
initReportsStreamListener();
